import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, MessageCircle, User } from "lucide-react";
import { NAV_LINKS, WHATSAPP_LINK } from "@/data/content";
import { scrollToSection, scrollToTop } from "@/lib/scroll";
import { Magnetic } from "./Magnetic";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [authUser, setAuthUser] = useState(null);
  const [active, setActive] = useState("");

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    const sections = NAV_LINKS.map((l) => document.getElementById(l.id)).filter(Boolean);
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => e.isIntersecting && setActive(e.target.id));
      },
      { rootMargin: "-45% 0px -50% 0px" }
    );
    sections.forEach((s) => observer.observe(s));
    fetch(`${API}/auth/me`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then(setAuthUser)
      .catch(() => {});
    return () => {
      window.removeEventListener("scroll", onScroll);
      observer.disconnect();
    };
  }, []);

  const go = (id) => {
    setOpen(false);
    scrollToSection(id);
  };

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: 0.2 }}
      className={`fixed inset-x-0 top-0 z-[90] transition-colors duration-500 ${
        scrolled
          ? "border-b border-[#22222A] bg-[#050505]/85 backdrop-blur-xl"
          : "bg-transparent"
      }`}
    >
      <nav className="mx-auto flex h-[72px] max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
        <button
          data-testid="nav-logo"
          onClick={scrollToTop}
          className="font-display text-lg font-extrabold tracking-tight text-zinc-100"
        >
          LUIZ<span className="text-[#E50914]">.DEV</span>
        </button>

        <div className="hidden items-center gap-8 lg:flex">
          {NAV_LINKS.map((link) => (
            <button
              key={link.id}
              data-testid={link.testid}
              onClick={() => go(link.id)}
              aria-current={active === link.id ? "true" : undefined}
              className={`link-underline font-mono-code text-xs uppercase tracking-[0.2em] transition-colors hover:text-zinc-100 ${
                active === link.id ? "text-zinc-100 is-active" : "text-zinc-400"
              }`}
            >
              {link.label}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-3">
          <a
            data-testid="nav-login-btn"
            href={authUser ? (authUser.role === "admin" ? "/admin" : "/conta") : "/entrar"}
            className="hidden items-center gap-2 rounded-full border border-[#2A2A33] px-5 py-2.5 font-mono-code text-xs uppercase tracking-widest text-zinc-300 transition-colors duration-300 hover:border-[#E50914]/60 hover:text-white sm:flex"
          >
            <User className="h-4 w-4 text-[#E50914]" />
            {authUser ? (authUser.role === "admin" ? "Painel" : "Minha Conta") : "Entrar"}
          </a>
          <Magnetic strength={0.25} className="hidden sm:inline-block">
            <a
              data-testid="nav-cta-whatsapp"
              href={WHATSAPP_LINK}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-red-glow btn-shine flex items-center gap-2 rounded-full bg-[#E50914] px-5 py-2.5 font-mono-code text-xs font-bold uppercase tracking-widest text-white"
            >
              <MessageCircle className="h-4 w-4" />
              Orçamento
            </a>
          </Magnetic>
          <button
            data-testid="nav-mobile-menu-btn"
            onClick={() => setOpen(!open)}
            className="flex h-10 w-10 items-center justify-center rounded-full border border-[#22222A] text-zinc-300 lg:hidden"
            aria-label="Abrir menu"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="overflow-hidden border-b border-[#22222A] bg-[#050505]/95 backdrop-blur-xl lg:hidden"
          >
            <div className="flex flex-col gap-1 px-6 py-4">
              {NAV_LINKS.map((link) => (
                <button
                  key={link.id}
                  data-testid={`mobile-${link.testid}`}
                  onClick={() => go(link.id)}
                  className="rounded-lg px-3 py-3 text-left font-mono-code text-sm uppercase tracking-widest text-zinc-300 transition-colors hover:bg-[#101014] hover:text-[#E50914]"
                >
                  {link.label}
                </button>
              ))}
              <a
                data-testid="mobile-nav-login-btn"
                href={authUser ? (authUser.role === "admin" ? "/admin" : "/conta") : "/entrar"}
                className="mt-1 flex items-center gap-2 rounded-lg px-3 py-3 font-mono-code text-sm uppercase tracking-widest text-zinc-300 transition-colors hover:bg-[#101014] hover:text-[#E50914]"
              >
                <User className="h-4 w-4 text-[#E50914]" />
                {authUser ? (authUser.role === "admin" ? "Painel Admin" : "Minha Conta") : "Entrar"}
              </a>
              <a
                data-testid="mobile-nav-cta-whatsapp"
                href={WHATSAPP_LINK}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-2 flex items-center justify-center gap-2 rounded-full bg-[#E50914] px-5 py-3 font-mono-code text-xs font-bold uppercase tracking-widest text-white"
              >
                <MessageCircle className="h-4 w-4" />
                Pedir Orçamento
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
